import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Pagination,
  MenuItem,
  Select,
} from "@mui/material";
import { flexRender } from "@tanstack/react-table";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { DETAILS_COLUMN_ID } from "./callsTableConfig";
import LoadingAnimation from "../reusables/LoadingAnimation";

const CallsTable = ({ table, onRowNavigate, isLoading }) => {
  const { pageIndex, pageSize } = table.getState().pagination;
  const filteredCount = table.getFilteredRowModel().rows.length;
  const pageCount = table.getPageCount();
  const from = filteredCount === 0 ? 0 : pageIndex * pageSize + 1;
  const to = Math.min((pageIndex + 1) * pageSize, filteredCount);
  const hasRows = table.getRowModel().rows.length > 0;

  const openCall = (event, link) => {
    if (!link) return;
    // Cmd (Mac) / Ctrl (Windows/Linux) → new tab
    if (event.metaKey || event.ctrlKey) {
      window.open(link, "_blank", "noopener,noreferrer");
      return;
    }
    onRowNavigate?.(link);
  };

  return (
    <div>
      <TableContainer className="w-full overflow-x-auto">
        <Table size="small" sx={{ minWidth: 720 }}>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} sx={{ backgroundColor: "#FAFAFA" }}>
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  return (
                    <TableCell
                      key={header.id}
                      sx={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#6B7280",
                        borderBottom: "1px solid #E5E7EB",
                        whiteSpace: "nowrap",
                        cursor: canSort ? "pointer" : "default",
                        userSelect: "none",
                        py: 1.5,
                        px: 2,
                      }}
                      onClick={
                        canSort
                          ? header.column.getToggleSortingHandler()
                          : undefined
                      }
                    >
                      <div className="inline-flex items-center gap-0.5">
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                        {sorted === "asc" && (
                          <ArrowDropUpIcon sx={{ fontSize: 18 }} />
                        )}
                        {sorted === "desc" && (
                          <ArrowDropDownIcon sx={{ fontSize: 18 }} />
                        )}
                      </div>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {isLoading && !hasRows && (
              <TableRow>
                <TableCell
                  colSpan={table.getVisibleLeafColumns().length}
                  sx={{ py: 8, textAlign: "center", borderBottom: "none" }}
                >
                  <div className="flex flex-col items-center justify-center gap-3">
                    <LoadingAnimation
                      style={{ width: 64, height: 64 }}
                      alt="Loading calls"
                    />
                    <span className="text-sm font-medium text-gray-500">
                      Loading calls…
                    </span>
                  </div>
                </TableCell>
              </TableRow>
            )}
            {!isLoading && !hasRows && (
              <TableRow>
                <TableCell
                  colSpan={table.getVisibleLeafColumns().length}
                  sx={{ py: 6, textAlign: "center", color: "#9CA3AF" }}
                >
                  No calls match your filters.
                </TableCell>
              </TableRow>
            )}
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                hover
                onClick={(e) => openCall(e, row.original.link)}
                onAuxClick={(e) => {
                  // Middle-click → new tab
                  if (e.button === 1 && row.original.link) {
                    e.preventDefault();
                    window.open(
                      row.original.link,
                      "_blank",
                      "noopener,noreferrer"
                    );
                  }
                }}
                sx={{
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "#F9FAFB" },
                }}
              >
                {row.getVisibleCells().map((cell) => {
                  if (cell.column.id === DETAILS_COLUMN_ID) {
                    return (
                      <TableCell
                        key={cell.id}
                        sx={{
                          borderBottom: "1px solid #E5E7EB",
                          py: 1.5,
                          px: 1,
                          width: 48,
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => openCall(e, row.original.link)}
                          aria-label="View call details"
                        >
                          <ChevronRightIcon />
                        </IconButton>
                      </TableCell>
                    );
                  }
                  return (
                    <TableCell
                      key={cell.id}
                      sx={{
                        borderBottom: "1px solid #E5E7EB",
                        py: 1.5,
                        px: 2,
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <div className="flex flex-col sm:flex-row justify-between items-center gap-3 px-4 py-3">
        <div className="flex items-center gap-3 text-sm text-gray-500">
          <span>
            Showing {from}–{to} of {filteredCount}
          </span>
          <Select
            size="small"
            value={pageSize}
            onChange={(e) => table.setPageSize(Number(e.target.value))}
            sx={{ fontSize: 13, height: 32 }}
          >
            {[12, 25, 50].map((n) => (
              <MenuItem key={n} value={n}>
                {n} / page
              </MenuItem>
            ))}
          </Select>
        </div>
        {pageCount > 1 && (
          <Pagination
            count={pageCount}
            page={pageIndex + 1}
            onChange={(_, value) => table.setPageIndex(value - 1)}
            color="primary"
            shape="rounded"
            size="small"
            siblingCount={1}
            boundaryCount={1}
          />
        )}
      </div>
    </div>
  );
};

export default CallsTable;
